import { EyeIcon, EyeSlashIcon } from '@heroicons/react/24/solid';
import { cva, type VariantProps } from 'class-variance-authority';
import {
  forwardRef,
  type InputHTMLAttributes,
  type ReactNode,
  useState,
} from 'react';
import { cn } from '@/utils/cn';

// 1) Omit 'size' from the native input attributes
type NativeInputProps = Omit<InputHTMLAttributes<HTMLInputElement>, 'size'>;

const containerVariants = cva(
  'relative flex items-center gap-4 rounded-md bg-dark-gray px-4 focus-within:ring-2 focus-within:ring-blue-gray',
  {
    variants: {
      error: {
        true: 'ring-2 ring-error',
        false: '',
      },
      disabled: {
        true: 'cursor-not-allowed bg-light-gray opacity-50',
        false: '',
      },
      readOnly: {
        true: 'bg-light-gray',
        false: '',
      },
    },
    defaultVariants: {
      error: false,
      disabled: false,
      readOnly: false,
    },
  }
);

const inputVariants = cva(
  'w-full readonly:cursor-default bg-transparent text-sm focus:outline-none focus:ring-0 disabled:cursor-not-allowed',
  {
    variants: {
      // 2) Keep your own "size" variant for controlling the height
      size: {
        sm: 'h-8',
        md: 'h-9',
        lg: 'h-12',
      },
    },
    defaultVariants: {
      size: 'lg',
    },
  }
);

// 3) Extend our custom NativeInputProps instead of the unmodified InputHTMLAttributes
export interface InputProps
  extends NativeInputProps,
    VariantProps<typeof inputVariants> {
  label?: string;
  name: string;
  leftIcon?: ReactNode;
  error?: string;
  wrapperClassName?: string;
}

export const Input = forwardRef<HTMLInputElement, InputProps>(
  (
    {
      label,
      name,
      leftIcon,
      error,
      type = 'text',
      className,
      wrapperClassName,
      size,
      ...props
    },
    ref
  ) => {
    const [passwordVisible, setPasswordVisible] = useState(false);

    const isPasswordField = type === 'password';
    const inputType = isPasswordField && passwordVisible ? 'text' : type;

    const togglePasswordVisibility = () => {
      setPasswordVisible((prev) => !prev);
    };

    return (
      <div className={cn('w-full', wrapperClassName)}>
        {label && (
          <label className="mb-1 block text-sm text-white" htmlFor={name}>
            {label}
          </label>
        )}

        <div
          className={cn(
            containerVariants({
              error: Boolean(error),
              disabled: props.disabled,
              readOnly: props.readOnly,
            }),
            className
          )}
        >
          {leftIcon && <>{leftIcon}</>}

          <input
            className={cn(
              inputVariants({
                size,
              }),
              className
            )}
            id={name}
            name={name}
            ref={ref}
            type={inputType}
            {...props}
          />

          {isPasswordField && (
            <button
              aria-label={passwordVisible ? 'Hide password' : 'Show password'}
              aria-pressed={passwordVisible}
              className="text-gray-500 hover:text-gray-300 focus:outline-none"
              onClick={togglePasswordVisibility}
              type="button"
            >
              {passwordVisible ? (
                <EyeSlashIcon aria-hidden="true" className="h-5 w-5" />
              ) : (
                <EyeIcon aria-hidden="true" className="h-5 w-5" />
              )}
            </button>
          )}
        </div>

        {error && <p className="mt-1 text-red text-sm">{error}</p>}
      </div>
    );
  }
);

Input.displayName = 'Input';
